// src/pages/ServicesPage.tsx
import { Link } from 'react-router-dom'
import { useRef } from 'react'
import SEOHead from '../components/SEOHead'
import ScrollReveal from '../components/ScrollReveal'
import styles from './ServicesPage.module.css'

const SERVICES = [
  {
    id: 'gyms', num: '01', label: 'Fitness & Gyms', icon: '💪',
    title: 'Gym Vending Machines in Edmonton',
    desc1: 'Your gym members work hard. They deserve the right nutrition at the right moment. FreshDispense places specialized fitness vending machines like Benvend, stocked with protein shakes, pre-workout supplements, electrolyte drinks, protein bars, and recovery products.',
    desc2: 'We partner with Edmonton gyms, CrossFit boxes, MMA studios, yoga studios, and fitness centres of all sizes to add a passive revenue stream that your members actually appreciate.',
    features: ['Protein shakes & bars', 'Pre-workout supplements', 'Electrolyte drinks', 'Recovery products', 'Healthy snacks', 'Post-workout nutrition'],
    cta: 'Get a Gym Placement',
    badge: '🏋️ Gym Vending',
    imgIcon: '💪',
  },
  {
    id: 'bars', num: '02', label: 'Bars & Nightlife', icon: '🌃',
    title: "Bar Vending Machines in Edmonton",
    desc1: "Edmonton's bar and nightlife scene is thriving. FreshDispense helps you capitalize on it with strategically placed vending machines offering vape products, accessories, and late-night convenience items your customers are already looking for.",
    desc2: 'No staff interaction required. Machines operate 24/7, generating revenue even when your team is at its busiest. Perfect for bars, clubs, lounges, and entertainment venues across YEG.',
    features: ['Vape devices & pods', 'Vape accessories', 'Late-night convenience', 'Age-verified dispensing', '24/7 passive revenue', 'Discreet placement'],
    cta: 'Get a Bar Placement',
    badge: '🍺 Bar Vending',
    imgIcon: '🌃',
  },
  {
    id: 'snacks', num: '03', label: 'Snacks & Beverages', icon: '🍬',
    title: 'Snack & Beverage Vending in Edmonton',
    desc1: 'From corporate offices to hotel lobbies, everyone needs quick, convenient snacks and drinks. FreshDispense places premium snack and beverage vending machines in high-traffic Edmonton locations.',
    desc2: 'We stock everything from classic favourites to healthier options — so your employees, guests, and customers are always satisfied without you lifting a finger.',
    features: ['Classic snacks & chips', 'Healthy alternatives', 'Hot & cold beverages', 'Energy drinks', 'Candy & chocolate', 'Fresh grab-and-go'],
    cta: 'Get a Snack Placement',
    badge: '🍬 Snack Vending',
    imgIcon: '🏢',
  },
  {
    id: 'custom', num: '04', label: 'Custom Solutions', icon: '🎯',
    title: 'Custom Vending Solutions for Any Edmonton Venue',
    desc1: "Have a unique location that doesn't fit a standard category? FreshDispense specializes in thinking outside the machine. We'll design a fully custom vending solution — unique product selection, machine style, and placement strategy.",
    desc2: 'Salons, medical clinics, universities, shopping centres, recreational facilities — if you have foot traffic in Edmonton, we can create a vending solution that adds value.',
    features: ['Fully custom product mix', 'Machine style selection', 'Branding options', 'Strategic placement', 'Ongoing optimization', 'Any venue type'],
    cta: 'Discuss My Location',
    badge: '⚡ Custom',
    imgIcon: '🎯',
  },
]

const VENUES = [
  { icon: '🏋️', name: 'Gyms & Fitness Studios', sub: 'CrossFit, yoga, weights, MMA' },
  { icon: '🍺', name: 'Bars & Nightclubs', sub: 'Lounges, clubs, pubs, sports bars' },
  { icon: '🏢', name: 'Offices & Workplaces', sub: 'Corporate, co-working, industrial' },
  { icon: '🏨', name: 'Hotels & Hospitality', sub: 'Hotels, motels, B&Bs, resorts' },
  { icon: '🛍', name: 'Retail & Shopping', sub: 'Malls, strip malls, boutiques' },
  { icon: '🎓', name: 'Education', sub: 'Colleges, universities, schools' },
  { icon: '💇', name: 'Salons & Spas', sub: 'Hair salons, nail bars, aesthetics' },
  { icon: '🏊', name: 'Recreational Facilities', sub: 'Pools, arenas, community centres' },
  { icon: '🎯', name: 'Any High-Traffic Venue', sub: 'If people are there, we can help' },
]

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  provider: {
    '@type': 'LocalBusiness',
    name: 'FreshDispense',
    url: 'https://www.freshdispense.com',
    address: { '@type': 'PostalAddress', addressLocality: 'Edmonton', addressRegion: 'AB', addressCountry: 'CA' },
  },
  serviceType: 'Vending Machine Placement',
  areaServed: { '@type': 'City', name: 'Edmonton' },
}

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.freshdispense.com/' },
    { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://www.freshdispense.com/services' },
  ],
}

export default function ServicesPage() {
  const barGalleryRef = useRef<HTMLDivElement>(null)
  const gymGalleryRef = useRef<HTMLDivElement>(null)

  const scrollGallery = (galleryRef: { current: HTMLDivElement | null }, direction: 'left' | 'right') => {
    const gallery = galleryRef.current
    if (!gallery) return
    const distance = gallery.clientWidth * 0.8
    gallery.scrollBy({ left: direction === 'left' ? -distance : distance, behavior: 'smooth' })
  }

  return (
    <>
      <SEOHead
        title="Vending Machine Services Edmonton | Gym, Bar & Office Vending | FreshDispense"
        description="FreshDispense offers custom vending machine placement services across Edmonton. Gym protein vending, bar vape vending, office snack vending & more. Free installation. YEG's top vending company."
        canonical="/services"
        keywords="vending machine services Edmonton, gym vending machine Edmonton, bar vending Edmonton, office vending Edmonton, snack vending Alberta, custom vending YEG"
        schema={[SERVICE_SCHEMA, BREADCRUMB_SCHEMA]}
      />

      <main className={`page-enter ${styles.main}`}>
        {/* ─── PAGE HERO ─── */}
        <section className={styles.pageHero} aria-label="Services header">
          <div className="container">
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className={styles.breadSep}>/</span>
              <span>Services</span>
            </nav>
            <ScrollReveal>
              <span className="badge badge-blue">
                <span className="badge-dot" />Vending Services Edmonton
              </span>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <h1 className={styles.pageH1}>
                Custom Vending Solutions<br />
                <span className="gradient-text">Built for Edmonton Venues</span>
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className={styles.pageSubtitle}>
                Every location is different. FreshDispense custom-matches every machine and product
                selection to your specific venue, customers, and goals across YEG and Alberta.
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* ─── SERVICE SECTIONS ─── */}
        {SERVICES.map((s, idx) => (
          <section
            key={s.id}
            id={s.id}
            className={`${styles.serviceSection} ${idx % 2 !== 0 ? styles.serviceSectionAlt : ''}`}
            aria-labelledby={`${s.id}-heading`}
          >
            <div className="container">
              <div className={`${styles.serviceLayout} ${idx % 2 !== 0 ? styles.serviceLayoutReverse : ''} ${s.id === 'custom' ? styles.serviceLayoutSingle : ''}`}>
                <div className={styles.serviceBody}>
                  <div className={styles.serviceHeader}>
                    <span className={styles.serviceNum}>{s.num}</span>
                    <span className="label-tag">{s.label}</span>
                    <h2 id={`${s.id}-heading`} className={styles.serviceTitle}>{s.title}</h2>
                  </div>
                  <ScrollReveal>
                    <p style={{ fontSize: 16, lineHeight: 1.8, marginBottom: '1rem' }}>{s.desc1}</p>
                    <p style={{ fontSize: 16, lineHeight: 1.8, marginBottom: '1.5rem' }}>{s.desc2}</p>
                  </ScrollReveal>
                  <ScrollReveal delay={100}>
                    <div className={styles.featureGrid}>
                      {s.features.map(f => (
                        <div key={f} className={styles.featureChip}>
                          <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓</span> {f}
                        </div>
                      ))}
                    </div>
                  </ScrollReveal>
                  <ScrollReveal delay={200}>
                    <Link to="/contact" className="btn btn-primary" style={{ marginTop: '1.5rem' }}>
                      {s.cta} →
                    </Link>
                  </ScrollReveal>
                </div>
                {s.id !== 'custom' && (
                  <ScrollReveal delay={200}>
                    <div className={`${styles.serviceImg} ${s.id === 'gyms' || s.id === 'snacks' || s.id === 'bars' ? styles.serviceImgGym : ''} ${s.id === 'snacks' ? styles.serviceImgSnack : ''} ${s.id === 'bars' ? styles.serviceImgBar : ''}`}>
                      {s.id !== 'snacks' && s.id !== 'gyms' && s.id !== 'bars' && <span className={styles.serviceImgBadge}>{s.badge}</span>}
                      {s.id === 'gyms' ? (
                      <div className={styles.serviceImgGalleryWrap}>
                        <button
                          type="button"
                          className={styles.galleryNavButton}
                          onClick={() => scrollGallery(gymGalleryRef, 'left')}
                          aria-label="Scroll gym images left"
                        >
                          ‹
                        </button>
                        <div className={styles.serviceImgGallery} ref={gymGalleryRef}>
                          <img src="/gymvend2.png" alt="Gym vending machine 1" className={styles.serviceImgPhotoSmall} />
                          <img src="/gymvend.png" alt="Gym vending machine 2" className={styles.serviceImgPhotoSmall} />
                        </div>
                        <button
                          type="button"
                          className={`${styles.galleryNavButton} ${styles.galleryNavButtonRight}`}
                          onClick={() => scrollGallery(gymGalleryRef, 'right')}
                          aria-label="Scroll gym images right"
                        >
                          ›
                        </button>
                      </div>
                    ) : s.id === 'snacks' ? (
                      <img src="/snackvend.png" alt="Snack vending machine" className={styles.serviceImgPhotoFill} />
                    ) : s.id === 'bars' ? (
                      <div className={styles.serviceImgGalleryWrap}>
                        <button
                          type="button"
                          className={styles.galleryNavButton}
                          onClick={() => scrollGallery(barGalleryRef, 'left')}
                          aria-label="Scroll bar images left"
                        >
                          ‹
                        </button>
                        <div className={styles.serviceImgGallery} ref={barGalleryRef}>
                          <img src="/BarVend.png" alt="Bar vending machine 1" className={styles.serviceImgPhotoSmall} />
                          <img src="/barvend2.png" alt="Bar vending machine 2" className={styles.serviceImgPhotoSmall} />
                          <img src="/barvend3.png" alt="Bar vending machine 3" className={styles.serviceImgPhotoSmall} />
                        </div>
                        <button
                          type="button"
                          className={`${styles.galleryNavButton} ${styles.galleryNavButtonRight}`}
                          onClick={() => scrollGallery(barGalleryRef, 'right')}
                          aria-label="Scroll bar images right"
                        >
                          ›
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 64 }}>{s.imgIcon}</div>
                        <p style={{ fontSize: 13, textAlign: 'center', padding: '0 2rem' }}>
                          Add your {s.label.toLowerCase()} photo here
                        </p>
                      </>
                    )}
                  </div>
                </ScrollReveal>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* ─── PRICING ─── */}
        <section className="section" aria-labelledby="pricing-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">Investment</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="pricing-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '0.75rem' }}>
                Simple, Transparent Pricing<br />for Edmonton Venues
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p style={{ fontSize: 17, maxWidth: 520, marginBottom: '3rem' }}>
                No upfront cost, no hidden fees. We invest in your location — you share in the revenue.
              </p>
            </ScrollReveal>
            <div className={styles.pricingCards}>
              <ScrollReveal>
                <div className={styles.pricingCard}>
                  <div className={styles.pricingPrice}>$0</div>
                  <div className={styles.pricingPeriod}>Upfront cost to your business</div>
                  <ul className={styles.pricingList}>
                    {['Machine purchase — on us', 'Full installation included', 'Initial product stocking included',
                      'Ongoing maintenance covered', 'Regular restocking handled', 'Technical support 7 days/week'].map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                    Learn More
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <div className={`${styles.pricingCard} ${styles.pricingFeatured}`}>
                  <div className={styles.pricingPrice} style={{ fontSize: 48 }}>Revenue<br />Share</div>
                  <div className={styles.pricingPeriod}>Qualified locations earn a % of every sale</div>
                  <ul className={styles.pricingList}>
                    {['Earn passively from every transaction', 'Monthly revenue reports',
                      'No work required from your team', 'Direct deposit or cheque',
                      'Performance reviewed quarterly', 'Transparent, straightforward terms'].map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                    Apply for Revenue Share →
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* ─── ALL VENUES ─── */}
        <section className={styles.venuesSection} aria-labelledby="venues-heading">
          <div className="container">
            <ScrollReveal><span className="label-tag">Venues We Serve</span></ScrollReveal>
            <ScrollReveal delay={100}>
              <h2 id="venues-heading" style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '2.5rem' }}>
                We Place Machines Everywhere<br />Across Edmonton
              </h2>
            </ScrollReveal>
            <div className={styles.venuesGrid}>
              {VENUES.map((v, i) => (
                <ScrollReveal key={v.name} delay={(i % 3) * 100}>
                  <div className={styles.venueCard}>
                    <div style={{ fontSize: 32, marginBottom: '0.75rem' }}>{v.icon}</div>
                    <h4 style={{ fontSize: 15, color: 'var(--text)', marginBottom: '0.4rem' }}>{v.name}</h4>
                    <p style={{ fontSize: 13 }}>{v.sub}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="section" aria-label="CTA">
          <div className="container">
            <ScrollReveal>
              <div className="cta-banner">
                <h2 style={{ fontSize: 'clamp(28px,4vw,52px)', marginBottom: '1rem' }}>
                  Ready to Add Vending to<br />Your Edmonton Business?
                </h2>
                <p style={{ fontSize: 18, maxWidth: 520, margin: '0 auto 2.5rem' }}>
                  Book a free consultation. We'll assess your location and find the perfect vending
                  solution at no cost to you.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/contact" className="btn btn-primary">Book Free Consultation →</Link>
                  <Link to="/about" className="btn btn-outline">About FreshDispense</Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </>
  )
}
